import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Cloud, Satellite, Sprout, Leaf, CheckCircle2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXTERNAL_SOURCES = [
  { key: 'weather', farmerDataKey: 'weather_data', farmerFetchedAtKey: 'weather_data_fetched_at',
    label: 'Weather Data', icon: Cloud, color: '#2563eb', bgColor: '#eff6ff',
    description: 'Rainfall deviation, seasonal summary, forecast' },
  { key: 'satellite', farmerDataKey: 'satellite_data', farmerFetchedAtKey: 'satellite_data_fetched_at',
    label: 'Satellite / NDVI', icon: Satellite, color: '#7c3aed', bgColor: '#f5f3ff',
    description: 'Vegetation indices, crop health, land use trends' },
  { key: 'pm_kisan', farmerDataKey: 'pm_kisan_data', farmerFetchedAtKey: 'pm_kisan_data_fetched_at',
    label: 'PM-KISAN', icon: Sprout, color: '#059669', bgColor: '#ecfdf5',
    description: 'Beneficiary status, payment history, scheme enrollment' },
  { key: 'soil_health', farmerDataKey: 'soil_health_data', farmerFetchedAtKey: 'soil_health_data_fetched_at',
    label: 'Soil Health', icon: Leaf, color: '#b45309', bgColor: '#fffbeb',
    description: 'Nutrient analysis, pH levels, soil health score' },
];

const tryParse = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-baseline py-0.5 text-[11px]">
    <span className="text-gray-500">{label}</span>
    <span className="font-mono text-gray-800 text-right">{String(value ?? 'N/A')}</span>
  </div>
);

const renderSourceDetails = (sourceKey, data) => {
  if (!data) return <div className="text-[11px] text-gray-400 italic">No details available</div>;

  if (sourceKey === 'weather') {
    return (
      <div className="space-y-0.5">
        <DetailRow label="Location" value={`${data.state || ''}${data.district ? ', ' + data.district : ''}`.trim() || 'N/A'} />
        <DetailRow label="Rainfall deviation" value={data.rainfall_deviation_pct !== undefined ? `${data.rainfall_deviation_pct}%` : null} />
        <DetailRow label="Seasonal summary" value={data.seasonal_summary} />
        <DetailRow label="Forecast (next 7d)" value={data.forecast_next_7d || data.next_7d_forecast} />
        <DetailRow label="Alert level" value={data.alert_level} />
      </div>
    );
  }

  if (sourceKey === 'satellite') {
    const vi = data.vegetation_indices || {};
    const ch = data.crop_health || {};
    return (
      <div className="space-y-0.5">
        <DetailRow label="Imagery date" value={data.imagery_date} />
        <DetailRow label="NDVI" value={vi.ndvi} />
        <DetailRow label="NDVI interpretation" value={vi.ndvi_interpretation} />
        <DetailRow label="EVI" value={vi.evi} />
        <DetailRow label="Crop health" value={ch.status} />
        <DetailRow label="Stress level" value={ch.stress_level} />
      </div>
    );
  }

  if (sourceKey === 'pm_kisan') {
    const payments = Array.isArray(data.payment_history) ? data.payment_history : [];
    return (
      <div className="space-y-0.5">
        <DetailRow label="Beneficiary status" value={data.beneficiary_status ? 'Active' : 'Inactive'} />
        <DetailRow label="Registration date" value={data.registration_date} />
        <DetailRow label="Total received" value={data.total_received !== undefined ? `Rs. ${Number(data.total_received).toLocaleString()}` : null} />
        <DetailRow label="Last payment" value={data.last_payment_date} />
        <DetailRow label="Installments on record" value={payments.length || 'N/A'} />
      </div>
    );
  }

  if (sourceKey === 'soil_health') {
    const an = data.soil_analysis || {};
    return (
      <div className="space-y-0.5">
        <DetailRow label="Card number" value={data.card_number} />
        <DetailRow label="Soil health score" value={data.soil_health_score} />
        <DetailRow label="Soil type" value={data.soil_type} />
        <DetailRow label="pH level" value={an.ph_level} />
        <DetailRow label="Nitrogen" value={an.nitrogen} />
        <DetailRow label="Phosphorus" value={an.phosphorus} />
        <DetailRow label="Potassium" value={an.potassium} />
        <DetailRow label="Organic carbon" value={an.organic_carbon} />
      </div>
    );
  }

  return <pre className="text-[10px] text-gray-500 overflow-x-auto">{JSON.stringify(data, null, 2).slice(0, 400)}</pre>;
};

const ExternalRefresh = ({ farmer, assessment, refreshAssessment, onUpdateAssessment }) => {
  const [loadingKey, setLoadingKey] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [farmerState, setFarmerState] = useState(farmer);
  const extRefresh = assessment?.external_data_refresh || {};

  const getSourceState = (source) => {
    const assessmentEntry = extRefresh[source.key];
    const parsedData = tryParse(farmerState?.[source.farmerDataKey]);
    const farmerHasData = !!parsedData;
    const farmerFetchedAt = farmerState?.[source.farmerFetchedAtKey];

    const assessmentAt = assessmentEntry?.last_refreshed_at;
    const lastAt = [assessmentAt, farmerFetchedAt].filter(Boolean).sort().reverse()[0] || null;

    const isAvailable = assessmentEntry?.status === 'REFRESHED' || farmerHasData;
    const origin = assessmentEntry?.status === 'REFRESHED'
      ? 'Refreshed in this assessment'
      : (farmerHasData ? 'From farmer profile' : null);

    return { isAvailable, lastAt, origin, parsedData };
  };

  const handleRefresh = async (sourceKey) => {
    if (!farmer || !assessment?.id) return;
    setLoadingKey(sourceKey);
    try {
      let endpoint;
      if (sourceKey === 'weather') {
        endpoint = `${API}/integrations/weather?state=${encodeURIComponent(farmer.residential_state || '')}&district=${encodeURIComponent(farmer.residential_district || '')}`;
      } else if (sourceKey === 'satellite') {
        endpoint = `${API}/integrations/satellite/${farmer.id}`;
      } else if (sourceKey === 'pm_kisan') {
        endpoint = `${API}/integrations/pm-kisan/${farmer.aadhaar_number}`;
      } else if (sourceKey === 'soil_health') {
        endpoint = `${API}/integrations/soil-health/${farmer.id}`;
      }

      if (endpoint) {
        await axios.get(endpoint);
      }

      const now = new Date().toISOString();
      const updatePayload = {
        external_data_refresh: {
          [sourceKey]: { last_refreshed_at: now, refreshed_by: 'officer', status: 'REFRESHED' }
        }
      };
      await onUpdateAssessment(updatePayload);
      await refreshAssessment();

      try {
        const fRes = await axios.get(`${API}/farmers/${farmer.id}`);
        if (fRes?.data) setFarmerState(fRes.data);
      } catch { /* non-fatal */ }

      toast.success(`${EXTERNAL_SOURCES.find(s => s.key === sourceKey)?.label} refreshed`);
    } catch (err) {
      toast.error(`Failed to refresh ${sourceKey}`);
    } finally {
      setLoadingKey(null);
    }
  };

  const refreshAll = async () => {
    for (const source of EXTERNAL_SOURCES) {
      await handleRefresh(source.key);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="external-refresh-title">
            External Data Refresh
          </h3>
          <p className="text-sm text-gray-500">
            Previously fetched data from the farmer profile is shown below. Expand any card to view details.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll} disabled={!!loadingKey} data-testid="refresh-all-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh All
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {EXTERNAL_SOURCES.map(source => {
          const Icon = source.icon;
          const { isAvailable, lastAt, origin, parsedData } = getSourceState(source);
          const isLoading = loadingKey === source.key;
          const isExpanded = !!expanded[source.key];

          return (
            <Card
              key={source.key}
              className={`transition-all ${isAvailable ? 'border-green-200' : 'border-gray-200'}`}
              data-testid={`ext-card-${source.key}`}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: source.bgColor }}>
                      <Icon className="h-4.5 w-4.5" style={{ color: source.color }} />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">{source.label}</div>
                      <div className="text-[10px] text-gray-400">{source.description}</div>
                    </div>
                  </div>
                  {isAvailable && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
                </div>

                {isAvailable && (
                  <div className="mb-2 space-y-1">
                    {lastAt && (
                      <div className="flex items-center gap-1 text-[10px] text-gray-500" data-testid={`ext-last-fetched-${source.key}`}>
                        <Clock className="h-3 w-3" />
                        Last fetched: {new Date(lastAt).toLocaleString()}
                      </div>
                    )}
                    {origin && (
                      <Badge className="text-[9px] bg-gray-100 text-gray-600 font-normal" data-testid={`ext-origin-${source.key}`}>
                        {origin}
                      </Badge>
                    )}
                  </div>
                )}

                {isAvailable && parsedData && (
                  <div className="mb-2">
                    <button
                      className="w-full flex items-center justify-between text-[11px] text-gray-600 hover:text-gray-900 py-1.5 border-t border-gray-100 mt-2"
                      onClick={() => setExpanded(p => ({ ...p, [source.key]: !p[source.key] }))}
                      data-testid={`ext-toggle-details-${source.key}`}
                    >
                      <span className="font-medium">{isExpanded ? 'Hide details' : 'View details'}</span>
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                    {isExpanded && (
                      <div className="mt-2 p-2 rounded-md bg-gray-50 border border-gray-100" data-testid={`ext-details-${source.key}`}>
                        {renderSourceDetails(source.key, parsedData)}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  variant={isAvailable ? 'outline' : 'default'}
                  size="sm"
                  className="w-full"
                  onClick={() => handleRefresh(source.key)}
                  disabled={isLoading}
                  style={!isAvailable ? { background: source.color } : {}}
                  data-testid={`refresh-${source.key}-btn`}
                >
                  {isLoading ? (
                    <SaakhSetuLoader size={16} />
                  ) : (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      {isAvailable ? 'Re-Refresh' : 'Refresh'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ExternalRefresh;
